import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { staffName, staffInitials } from "@/lib/staffFields";
import { CACES_SECTION } from "@/lib/staffFields";
import StaffModal from "@/components/StaffModal";
import StaffDetailModal from "@/components/StaffDetailModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Trash2, Link2, Copy, Send, Pencil, Download, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

const DEFAULT_COLUMNS = [
  { key: "prenom", label: "Prénom", cls: "hidden md:table-cell" },
  { key: "telephone", label: "Téléphone", cls: "hidden lg:table-cell", mono: true },
  { key: "email", label: "Mail", cls: "hidden lg:table-cell" },
  { key: "ville", label: "Provenance", cls: "hidden xl:table-cell" },
  { key: "autres_zones", label: "Autres zones d'intervention", cls: "hidden xl:table-cell" },
  { key: "date_limite_visite_medicale", label: "Validité Visite Médicale", cls: "hidden xl:table-cell", mono: true },
  { key: "doc_synology", label: "Doc dans Synology", cls: "hidden xl:table-cell", bool: true },
  { key: "preference_poste", label: "Préférence de poste", cls: "hidden xl:table-cell" },
  { key: "caces", label: "Dispose du CACES", cls: "hidden xl:table-cell" },
  { key: "caces_validite", label: "Validité du CACES", cls: "hidden xl:table-cell", mono: true },
  { key: "caces_type", label: "Type de CACES", cls: "hidden xl:table-cell" },
  { key: "caces_doc_synology", label: "Document dans Synology", cls: "hidden xl:table-cell", bool: true },
];

export default function StaffPage({ apiBase = "/staff", title = "Intermittents", addLabel = "Intermittent", showRegistration = true, exportName = "Sal-DCL-export.txt", regPath = "/inscription", columns = DEFAULT_COLUMNS, extraSections = [CACES_SECTION] } = {}) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [linkOpen, setLinkOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sort, setSort] = useState({ key: "nom", dir: "asc" });

  const registrationLink = `${window.location.origin}${regPath}`;

  const sortVal = (s, key) => (key === "nom" ? staffName(s) : (s[key] ?? "").toString()).toLowerCase();
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(apiBase);
      setItems(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) =>
      [s.nom, s.prenom, s.ville, s.profession, s.email, s.telephone]
        .some((v) => (v || "").toLowerCase().includes(q)));
  }, [items, query]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const doDelete = async () => {
    try {
      await api.delete(`${apiBase}/${toDelete.id}`);
      toast.success("Fiche supprimée");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(registrationLink);
      toast.success("Lien d'inscription copié");
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const sendLink = async () => {
    if (!sendEmail.trim()) { toast.error("Email requis"); return; }
    setSending(true);
    try {
      await api.post(`${apiBase}/send-registration-link`, { email: sendEmail.trim(), link: registrationLink });
      toast.success("Lien envoyé par email");
      setSendEmail("");
      setLinkOpen(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const openDetail = async (item) => {
    try {
      const { data } = await api.get(`${apiBase}/${item.id}`);
      setDetail(data);
    } catch {
      setDetail(item);
    }
  };

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selected.has(s.id));

  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allFilteredSelected) filtered.forEach((s) => n.delete(s.id));
    else filtered.forEach((s) => n.add(s.id));
    return n;
  });

  const doExport = async () => {
    const ids = [...selected];
    if (!ids.length) { toast.error("Sélectionnez au moins une fiche"); return; }
    try {
      const res = await api.post(`${apiBase}/export`, { ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${ids.length} fiche${ids.length > 1 ? "s" : ""} exportée${ids.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filtered.length} · {title}
            {query ? ` (sur ${items.length})` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={doExport} disabled={selected.size === 0} data-testid="btn-export-staff">
            <Download className="h-4 w-4 mr-1.5" /> Exporter{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
          {showRegistration && (
            <Button variant="outline" onClick={() => setLinkOpen(true)} data-testid="btn-registration-link">
              <Link2 className="h-4 w-4 mr-1.5" /> Lien d'inscription
            </Button>
          )}
          <Button onClick={() => { setEditing(null); setModalOpen(true); }}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="btn-add-staff">
            <Plus className="h-4 w-4 mr-1.5" /> {addLabel}
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher (nom, ville, métier, email...)"
          className="pl-9" data-testid="staff-search" />
      </div>

      <div className="rounded-xl border border-border overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-muted-foreground">
            <tr className="text-left">
              <th className="px-4 py-3 w-10">
                <Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll}
                  data-testid="staff-select-all" aria-label="Tout sélectionner" />
              </th>
              <th className="px-4 py-3 font-medium">
                <button className="flex items-center gap-1 hover:text-foreground transition-colors" onClick={() => toggleSort("nom")} data-testid="sort-nom">
                  Nom
                  {sort.key === "nom" ? (sort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />) : <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />}
                </button>
              </th>
              {columns.map((col) => (
                <th key={col.key} className={`px-4 py-3 font-medium border-l border-border/50 ${col.cls || ""}`}>
                  <button className="flex items-center gap-1 hover:text-foreground transition-colors" onClick={() => toggleSort(col.key)} data-testid={`sort-${col.key}`}>
                    {col.label}
                    {sort.key === col.key ? (sort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />) : <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />}
                  </button>
                </th>
              ))}
              <th className="px-4 py-3 font-medium text-right border-l border-border/50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-secondary/30 transition-colors cursor-pointer"
                onClick={() => openDetail(s)} data-testid={`staff-row-${s.id}`}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleOne(s.id)}
                    data-testid={`staff-select-${s.id}`} aria-label={`Sélectionner ${staffName(s)}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center font-display font-bold text-black text-[11px] shrink-0"
                      style={{ background: s.color || "#F59E0B" }}>
                      {staffInitials(s)}
                    </div>
                    <span className="font-medium text-foreground hover:text-primary" data-testid={`staff-name-${s.id}`}>{staffName(s)}</span>
                  </div>
                </td>
                {columns.map((col) => (
                  <td key={col.key} className={`px-4 py-3 text-muted-foreground border-l border-border/50 ${col.cls || ""} ${col.mono ? "tabular-nums" : ""}`}>
                    {col.bool ? (s[col.key] ? "Oui" : "Non") : ((s[col.key] ?? "").toString().trim() || "—")}
                  </td>
                ))}
                <td className="px-4 py-3 text-right border-l border-border/50" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => { setEditing(s); setModalOpen(true); }} className="text-muted-foreground hover:text-primary p-1.5" data-testid={`edit-staff-${s.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => setToDelete(s)} className="text-muted-foreground hover:text-destructive p-1.5" data-testid={`delete-staff-${s.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={columns.length + 3} className="px-4 py-10 text-center text-muted-foreground">Aucun résultat.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <StaffModal open={modalOpen} onOpenChange={setModalOpen} item={editing} onSaved={load} apiBase={apiBase} extraSections={extraSections} />
      <StaffDetailModal open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)} item={detail} apiBase={apiBase}
        extraSections={extraSections} onEdit={() => { setEditing(detail); setDetail(null); setModalOpen(true); }} />

      {showRegistration && (
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-lg" data-testid="registration-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Lien d'inscription</DialogTitle>
            <DialogDescription>Partagez ce lien pour compléter la fiche d'inscription.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input readOnly value={registrationLink} data-testid="registration-link-input" className="text-xs" />
              <Button variant="outline" onClick={copyLink} data-testid="copy-registration-link">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Envoyer par email</Label>
              <div className="flex items-center gap-2">
                <Input value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="email@exemple.fr" type="email"
                  data-testid="send-link-email" />
                <Button onClick={sendLink} disabled={sending} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="send-registration-link">
                  <Send className="h-4 w-4 mr-1.5" /> {sending ? "..." : "Envoyer"}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette fiche ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete && staffName(toDelete)} » sera définitivement supprimé de la base.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-staff">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
